import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Checkbox } from '@shared/ui/atoms/Checkbox';
import { BodyError, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import TextField from '@shared/ui/atoms/TextField';
import { validateEmail } from '@shared/utils/Validators';
import { login, selectLoginErrorMessage } from './login.duck';
import Colours from '@shared/ui/atoms/Colours';
import { Column } from '@shared/ui/atoms/StructuralLayout';
import { SpaceBetweenRow } from '@features/settings/pages/StyleComponents';
import { Divider } from '@shared/ui/atoms/Divider';
import { useGoogleLogin } from "@react-oauth/google";
import { AuthType, clearLoginStorage, persistAuthType } from "@shared/utils/AuthUtils";
import { Auth } from "@shared/services/Auth";
import LoginErrorMessage from "./LoginErrorMessage";
import { TrackJS } from "trackjs";
import { MicrosoftLoginButton } from './MicrosoftLoginButton';
import { MicrosoftTeamsLoginButton } from "./MicrosoftTeamsLoginButton";
import { RequestAdminConsentButton } from "./RequestAdminConsentButton";
import { NotificationMessages } from "@shared/models/notification.models";
import { getParameterByName } from "@shared/utils/UrlUtils";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { selectIsInTeams, selectTenantInfo } from "@shared/store/auth.duck";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

const GOOGLE = 'Google';

export default function StandardLogin() {
  const dispatch = useDispatch();
  const [rememberMe, setRememberMe] = useState<boolean>(localStorage.getItem('rememberMe') === 'true');
  const [email, setEmail] = useState(localStorage.getItem('rememberMeEmail') || '');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [password, setPassword] = useState('');
  const [generalErrorCode, setGeneralErrorCode] = useState('');
  const [attemptedLoginMethod, setAttemptedLoginMethod] = useState('');
  const [showAllAuthMethods, setShowAllAuthMethods] = useState(false);
  const {t} = useTranslation();

  const errorMessage = useSelector(selectLoginErrorMessage);
  const tenantInfo = useSelector(selectTenantInfo);
  const isInTeams = useSelector(selectIsInTeams);


  useEffect(() => {
    if (rememberMe && !!email) {
      localStorage.setItem('rememberMeEmail', email);
    }
  }, [rememberMe, email]);

  useEffect(() => {
    const notification = getParameterByName('not', window.location.href);
    if (notification) {
      setGeneralErrorCode(notification)
    }
  }, [dispatch]);

  const handleSubmit = (event: any) => {
    event.preventDefault();
    if (!email || !validateEmail(email)) {
      setEmailError(i18next.t('login.valid-email-address-error') as string);
    } else {
      setEmailError('');
    }

    if (rememberMe) {
      localStorage.setItem('rememberMeEmail', email);
    }

    if (!password) {
      setPasswordError(i18next.t('login.enter-password', 'test') as string);
    } else {
      setPasswordError('');
    }

    if (!!email && !!password) {
      setAttemptedLoginMethod('Team Today');
      persistAuthType(AuthType.Standard, dispatch);
      dispatch(login({
        email: email.trim(),
        password
      }));
    }
  }

  const onRememberMe = (value: boolean) => {
    setRememberMe(value);
    localStorage.setItem('rememberMe', `${value}`);
    if (value) {
      localStorage.setItem('rememberMeEmail', `${value}`);
    }
  }

  const responseGoogle = (tokenResponse: any) => {
    clearLoginStorage();
    setAttemptedLoginMethod(GOOGLE);
    persistAuthType(AuthType.Google, dispatch);
    localStorage.setItem('idToken', tokenResponse.access_token)
    localStorage.setItem('accessToken', tokenResponse.access_token)
    Auth.current(true)
      .then(() => window.location.href = '/whereabouts/team')
      .catch((err) => {
        TrackJS?.track(err);
        console.error(err);
        setGeneralErrorCode('goog');
      });
  }

  const failedGoogleResponse = (error: any) => {
    TrackJS?.track('failed google response')
    if (error) {
      TrackJS?.track(error)
    }
    setAttemptedLoginMethod(GOOGLE);
    setGeneralErrorCode('goog');
  }

  const googleLogin = useGoogleLogin({
    onSuccess: responseGoogle,
    onError: failedGoogleResponse,
    scope: 'https://www.googleapis.com/auth/calendar.events',
  });

  if (tenantInfo && !showAllAuthMethods) {
    return <>
      {!!generalErrorCode && <LoginErrorMessage errorCode={generalErrorCode} loginMethod={attemptedLoginMethod} />}

      <BodyRegular style={{marginBottom: 16, marginTop: 16}}>{t('login.company-already-has-account')}</BodyRegular>

      {isInTeams ? <MicrosoftTeamsLoginButton onError={setGeneralErrorCode} /> : <MicrosoftLoginButton />}
      {isInTeams && generalErrorCode === NotificationMessages.consentRequired && (
        <RequestAdminConsentButton />
      )}

      <div className="auth__backLink">
        <div onClick={() => {}} className="noDecoration">
          <BodyRegular weight={600}
                       style={{cursor: 'pointer'}}
                       colour={Colours.blue}
                       onClick={() => setShowAllAuthMethods(true)}>{t('login.try-another-method')}</BodyRegular>
        </div>
      </div>
    </>
  }

  return (
    <>
      {!!generalErrorCode && <LoginErrorMessage errorCode={generalErrorCode} loginMethod={attemptedLoginMethod} />}

      <BodyRegular style={{marginBottom: 16}}>{t('login.signup-with-microsoft')} <strong>{isInTeams ? t('login.microsoft') : t('login.microsoft-or-google')}</strong> {t('login.sync-own-calendars')}</BodyRegular>

      {isInTeams ? <MicrosoftTeamsLoginButton onError={setGeneralErrorCode} /> : <MicrosoftLoginButton />}
      {isInTeams && generalErrorCode === NotificationMessages.consentRequired && (
        <RequestAdminConsentButton />
      )}


      {!isInTeams &&
        <OutlineButton dataTest="googleLogin"
                       className="login__button"
                       text={'login.with-google'}
                       borderColour={Colours.black}
                       click={() => googleLogin()}
                       fullWidth={true}
                       size={ButtonSize.Large}
                       imageUrl="/assets/icons/google-logo.svg"/>
      }

      <SpaceBetweenRow style={{alignItems: 'center', marginBottom: 16, marginTop: 16}}>
        <Divider width={68} color={Colours.mildGrey} />
        <BodyRegular style={{margin: 0}} weight={600}>{t('login.or-login-with-email')}</BodyRegular>
        <Divider width={68} color={Colours.mildGrey} />
      </SpaceBetweenRow>

      {!!errorMessage &&
      <Column>
        <BodyError data-test="loginPage-error" weight={400} className="login__error"><img src="/assets/icons/error.svg"
                                                                                          alt="Error"/>{errorMessage?.title ?? ''}
        </BodyError>
        <BodyError size={16} weight={400} className="login__error">{errorMessage?.description ?? ''}</BodyError>
      </Column>
      }
      <form className="auth__form" onSubmit={(e) => e.preventDefault()}>

        <TextField dataTest="emailInput"
                   type="email"
                   name="email"
                   label={'login.email-address'}
                   value={email}
                   onChange={e => setEmail(e)}
                   fullWidth={true}
                   error={emailError}
                   enableAutoComplete={true}/>

        <TextField dataTest="passwordInput"
                   type="password"
                   name="password"
                   label={'login.password'}
                   value={password}
                   fullWidth={true}
                   onChange={e => setPassword(e)}
                   error={passwordError}
                   enableAutoComplete={true}/>

        <div className="login__formControls">
          <Checkbox value={!!rememberMe} text={'login.remember'} onChange={(value: boolean) => onRememberMe(value)}/>
          <Link className="noDecoration" to="/forgot-password">
            <BodySmall colour={Colours.blue}>{t('forgot-password.forgot-password')}</BodySmall>
          </Link>
        </div>
        <PrimaryButton fullWidth={true}
                       dataTest="submit"
                       className="login__button"
                       text={'login.login'}
                       buttonType="submit"
                       click={handleSubmit}/>
      </form>

      <div className="auth__backLink">
        <Link to="/register" className="noDecoration">
          <BodyRegular weight={600} colour={Colours.blue}>{t('login.no-account')}</BodyRegular>
        </Link>
      </div>
    </>
  )
}
